import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'

import { useAppSelector } from 'src/store/hooks'
import {
  fetchLinkedServices,
  selectLinkedServiceById,
  selectLinkedServicesStatus,
} from 'src/v3/manage/manage.slice'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { getS3ImageFilePath } from 'src/v3/utilities/image-upload'
import {
  fetchManageEventCategories,
  fetchManageEventTemplateJoins,
  fetchManageEventTemplates,
  selectAccreditationLabelByEvent,
  selectManageEventCategoriesStatus,
  selectManageEventCategoryNameMap,
  selectManageEventTemplateJoinsStatus,
  selectManageEventTemplatesStatus,
  selectTemplateLabelByEvent,
  type EventType,
  type ManageEvent,
} from '../manage-events.slice'
import { displayCategoryName } from '../utils/category'

type Props = {
  event: ManageEvent
}

let locationLabel = (eventType: EventType): string => {
  if ('inPerson' in eventType) return 'In-person'
  if ('virtual' in eventType) return 'Virtual'
  return 'Hybrid'
}

let attendanceLabel = (
  mechanism: ManageEvent['attendanceTrackingMechanism'],
): string =>
  mechanism.quizPasscode != null ? 'Quiz passcode' : 'Manager tracked'

type FieldProps = {
  label: string
  value: string | number | null | undefined
  multiline?: boolean
}

let subLabelSx = {
  fontSize: 12,
  color: 'rgba(255,255,255,0.55)',
  letterSpacing: '0.05em',
}

let Field = ({ label, value, multiline }: FieldProps) => (
  <Box
    component="div"
    className="py-4"
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' },
      gap: { xs: 0.5, sm: 6 },
      alignItems: { xs: 'start', sm: 'baseline' },
      borderBottom: '1px solid rgba(255,255,255,0.08)',
    }}
  >
    <Typography component="dt" className="uppercase" sx={subLabelSx}>
      {label}
    </Typography>
    <Typography
      component="dd"
      className="min-w-0 m-0"
      sx={{
        fontSize: 14,
        color: 'rgba(255,255,255,0.85)',
        lineHeight: 1.6,
        whiteSpace: multiline ? 'pre-wrap' : undefined,
      }}
    >
      {value ?? '—'}
    </Typography>
  </Box>
)

type EventImageProps = {
  label: string
  src: string | null
  alt: string
  aspectRatio: string
  emptyLabel: string
}

let EventImage = ({
  label,
  src,
  alt,
  aspectRatio,
  emptyLabel,
}: EventImageProps) => (
  <Box>
    <Typography className="block mb-2 uppercase shrink-0" sx={subLabelSx}>
      {label}
    </Typography>
    {src ? (
      <Box
        component="img"
        src={getS3ImageFilePath(src)}
        alt={alt}
        className="object-cover w-full rounded"
        sx={{ aspectRatio }}
      />
    ) : (
      <Box
        className="flex items-center justify-center w-full rounded"
        sx={{ aspectRatio, bgcolor: 'background.paperDark' }}
      >
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          {emptyLabel}
        </Typography>
      </Box>
    )}
  </Box>
)

let EventDetailsTab = ({ event }: Props) => {
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let linkedService = useAppSelector(
    selectLinkedServiceById(event.linkedServiceID),
  )
  let categoriesStatus = useAppSelector(selectManageEventCategoriesStatus)
  let categoryNameMap = useAppSelector(selectManageEventCategoryNameMap)
  let accreditationLabel = useAppSelector(
    selectAccreditationLabelByEvent(event.id),
  )
  let templatesStatus = useAppSelector(selectManageEventTemplatesStatus)
  let templateJoinsStatus = useAppSelector(selectManageEventTemplateJoinsStatus)
  let templateLabel = useAppSelector(selectTemplateLabelByEvent(event.id))

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)
  useAutoFetch(categoriesStatus, fetchManageEventCategories)
  useAutoFetch(templatesStatus, fetchManageEventTemplates)
  useAutoFetch(templateJoinsStatus, fetchManageEventTemplateJoins)

  return (
    <Box className="flex flex-col gap-6">
      <Paper sx={{ px: 3, py: '2px' }}>
        <Box component="dl" className="m-0">
          <Field
            label="Linked service"
            value={linkedService?.name ?? event.linkedServiceID}
          />
          <Field label="Sessions" value={event.sessionCount} />
          <Field
            label="Category"
            value={displayCategoryName(event.category, categoryNameMap)}
          />
          <Field
            label="CE hours"
            value={event.hours != null ? `${event.hours} hrs` : null}
          />
          <Field label="Description" value={event.description} multiline />
          <Field label="Location" value={locationLabel(event.eventType)} />
          <Field
            label="Attendance tracking"
            value={attendanceLabel(event.attendanceTrackingMechanism)}
          />
          <Field label="Accreditation" value={accreditationLabel} />
          <Field label="Certificate template" value={templateLabel} />
        </Box>
      </Paper>

      <Paper className="p-6">
        <Typography variant="subtitle1" className="mb-4 font-medium">
          Event images
        </Typography>
        <Box
          className="grid items-end gap-6"
          sx={{ gridTemplateColumns: { xs: '1fr', sm: '9fr 4fr' } }}
        >
          <EventImage
            label="Banner"
            src={event.banner}
            alt={`${event.name} banner`}
            aspectRatio="4/1"
            emptyLabel="No banner image"
          />
          <EventImage
            label="Thumbnail"
            src={event.thumbnail}
            alt={`${event.name} thumbnail`}
            aspectRatio="16/9"
            emptyLabel="No thumbnail"
          />
        </Box>
      </Paper>
    </Box>
  )
}

export default EventDetailsTab
